/**
 * WizardServiceScreen Component
 * Service selection step for the scheduling wizard
 * Based on WalkInModal service selection functionality
 */

import React, { useState, useEffect, useRef } from 'react';
import { 
  Search, 
  Clock, 
  DollarSign, 
  Check, 
  Plus, 
  ShoppingCart,
  Loader2
} from 'lucide-react';
import { useWizardStore } from '../../stores/wizardStore';
import { getCategories, getServicesByCategory } from '../../services/categoryService';
import { ServiceVariations } from '../VariationChip';
import { getServiceVariations, calculateFinalPrice, calculateFinalDuration } from '../../services/serviceVariationsService';

const WizardServiceScreen = () => {
  const firstInputRef = useRef(null);
  
  // State for service selection
  const [serviceSearchQuery, setServiceSearchQuery] = useState('');
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);
  
  // State for service variations
  const [serviceVariations, setServiceVariations] = useState({}); // { serviceId: [variationGroups] }
  const [selectedVariations, setSelectedVariations] = useState({}); // { serviceId: { groupId: variation } }
  const [expandedServices, setExpandedServices] = useState(new Set()); // Set of expanded service IDs
  
  // Wizard store
  const {
    selectedServices,
    setSelectedServices,
    goToNextStep,
    canProceedToStep,
    setLoading,
    setError,
    clearError
  } = useWizardStore();

  // Load categories and services on component mount
  useEffect(() => {
    loadServicesData();
  }, []);

  // Focus search input when component mounts
  useEffect(() => {
    if (firstInputRef.current) {
      firstInputRef.current.focus();
    }
  }, []);

  const loadServicesData = async () => {
    try {
      setLoadingServices(true);
      clearError();
      
      const categoriesData = await getCategories();
      setCategories(categoriesData);
      
      // Load all services from all categories
      const allServices = [];
      for (const category of categoriesData) {
        const categoryServices = await getServicesByCategory(category.id);
        allServices.push(...categoryServices);
      }
      
      setServices(allServices);
    } catch (error) {
      setError('Erro ao carregar serviços. Tente novamente.');
    } finally {
      setLoadingServices(false);
    }
  };

  // Filter services based on search query
  const filteredServices = services.filter(service =>
    service.name.toLowerCase().includes(serviceSearchQuery.toLowerCase()) ||
    service.category_name?.toLowerCase().includes(serviceSearchQuery.toLowerCase())
  );

  // Format price
  const formatPrice = (price) => {
    if (!price) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(parseFloat(price));
  };

  // Format duration
  const formatDuration = (minutes) => {
    if (!minutes) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
    }
    return `${mins}min`;
  };

  // Load variations for a service
  const loadServiceVariations = async (serviceId) => {
    try {
      const variations = await getServiceVariations(serviceId);
      setServiceVariations(prev => ({
        ...prev,
        [serviceId]: variations
      }));
    } catch (err) {
      // Silently fail - variations are optional
      setServiceVariations(prev => ({
        ...prev,
        [serviceId]: []
      }));
    }
  };

  // Toggle service expansion to show variations
  const toggleServiceExpansion = (serviceId) => {
    const newExpanded = new Set(expandedServices);
    if (expandedServices.has(serviceId)) {
      newExpanded.delete(serviceId);
    } else {
      newExpanded.add(serviceId);
      // Load variations when expanding
      if (!serviceVariations[serviceId]) {
        loadServiceVariations(serviceId);
      }
    }
    setExpandedServices(newExpanded);
  };

  // Handle variation selection - also adds/updates service in cart
  const handleVariationSelect = (serviceId, groupId, variation) => {
    // Update selected variations state
    const newVariations = {
      ...selectedVariations,
      [serviceId]: {
        ...selectedVariations[serviceId],
        [groupId]: variation
      }
    };
    setSelectedVariations(newVariations);

    // Find the service
    const service = services.find(s => s.id === serviceId);
    if (!service) {
      return;
    }

    // Calculate final price and duration with the new variation
    const finalPrice = calculateFinalPrice(service.price, newVariations[serviceId] || {});
    const finalDuration = calculateFinalDuration(service.duration_minutes, newVariations[serviceId] || {});

    // Build the primary variation object
    const variations = serviceVariations[serviceId] || [];
    const group = variations.find(g => g.id === groupId);
    const primaryVariation = {
      id: variation.id,
      name: variation.name,
      groupName: group?.name || '',
      price_delta: variation.price_delta || 0,
      duration_delta: variation.duration_delta || 0
    };

    // Build service with variation
    const serviceWithVariation = {
      ...service,
      price: finalPrice,
      duration_minutes: finalDuration,
      selectedVariation: primaryVariation,
      selectedVariations: newVariations[serviceId] || {},
      variation_id: variation.id
    };

    // Update or add to selected services
    const existingIndex = selectedServices.findIndex(s => s.id === serviceId);

    if (existingIndex >= 0) {
      // Update existing service with new variation
      const updatedServices = [...selectedServices];
      updatedServices[existingIndex] = serviceWithVariation;
      setSelectedServices(updatedServices);
    } else {
      // Add new service with variation
      setSelectedServices([...selectedServices, serviceWithVariation]);
    }
  };

  // Handle variation toggle for multiple choice groups
  const handleVariationToggle = (serviceId, groupId, variation) => {
    setSelectedVariations(prev => {
      const serviceVariations = prev[serviceId] || {};
      const currentSelection = serviceVariations[groupId] || [];
      
      let newSelection;
      if (Array.isArray(currentSelection)) {
        // Multiple choice - toggle in array
        const isSelected = currentSelection.some(v => v.id === variation.id);
        if (isSelected) {
          newSelection = currentSelection.filter(v => v.id !== variation.id);
        } else {
          newSelection = [...currentSelection, variation];
        }
      } else {
        // Convert to array for multiple choice
        newSelection = [variation];
      }
      
      return {
        ...prev,
        [serviceId]: {
          ...serviceVariations,
          [groupId]: newSelection
        }
      };
    });
  };

  // Get service final price including variations
  const getServiceFinalPrice = (service) => {
    const serviceVariationSelections = selectedVariations[service.id] || {};
    return calculateFinalPrice(service.price, serviceVariationSelections);
  };

  // Get the primary selected variation for API compatibility
  const getPrimaryVariation = (serviceId) => {
    const serviceVariationSelections = selectedVariations[serviceId] || {};
    const variations = serviceVariations[serviceId] || [];

    // Find the first selected variation (non-array)
    for (const [groupId, selection] of Object.entries(serviceVariationSelections)) {
      if (selection && !Array.isArray(selection)) {
        // Find the group to get context
        const group = variations.find(g => g.id === groupId);
        return {
          id: selection.id,
          name: selection.name,
          groupName: group?.name || '',
          price_delta: selection.price_delta || 0,
          duration_delta: selection.duration_delta || 0
        };
      } else if (Array.isArray(selection) && selection.length > 0) {
        // For multiple choice, return first selected
        const group = variations.find(g => g.id === groupId);
        return {
          id: selection[0].id,
          name: selection[0].name,
          groupName: group?.name || '',
          price_delta: selection[0].price_delta || 0,
          duration_delta: selection[0].duration_delta || 0
        };
      }
    }
    return null;
  };

  // Get display name for service with variation
  const getServiceDisplayName = (service) => {
    const primaryVariation = getPrimaryVariation(service.id);
    if (primaryVariation && primaryVariation.name && primaryVariation.name !== 'Padrão') {
      return `${service.name} - ${primaryVariation.name}`;
    }
    return service.name;
  };

  // Toggle service selection
  const toggleService = async (service) => {
    const isSelected = selectedServices.some(s => s.id === service.id);

    if (isSelected) {
      setSelectedServices(selectedServices.filter(s => s.id !== service.id));
      // Clear variations when deselecting service
      setSelectedVariations(prev => {
        const newVariations = { ...prev };
        delete newVariations[service.id];
        return newVariations;
      });
      // Collapse the variations panel
      setExpandedServices(prev => {
        const newExpanded = new Set(prev);
        newExpanded.delete(service.id);
        return newExpanded;
      });
    } else {
      // Check if we need to load variations first
      let variations = serviceVariations[service.id];

      // If variations haven't been loaded, load them now
      if (variations === undefined) {
        try {
          const loadedVariations = await getServiceVariations(service.id);
          setServiceVariations(prev => ({
            ...prev,
            [service.id]: loadedVariations
          }));
          variations = loadedVariations;
        } catch (err) {
          variations = [];
        }
      }

      const hasVariations = variations && variations.length > 0;
      const hasSelectedVariation = selectedVariations[service.id] && Object.keys(selectedVariations[service.id]).length > 0;

      // If service has variations but none selected, expand to show them
      if (hasVariations && !hasSelectedVariation) {
        // Expand to show variations - don't add to cart yet
        setExpandedServices(prev => new Set([...prev, service.id]));
        return;
      }

      // Calculate final price and duration including any selected variations
      const finalPrice = getServiceFinalPrice(service);
      const serviceVariationSelections = selectedVariations[service.id] || {};
      const finalDuration = calculateFinalDuration(service.duration_minutes, serviceVariationSelections);

      // Get primary variation for API compatibility
      const primaryVariation = getPrimaryVariation(service.id);

      const serviceWithVariations = {
        ...service,
        price: finalPrice,
        duration_minutes: finalDuration,
        selectedVariation: primaryVariation, // For API - single variation object with id, name
        selectedVariations: serviceVariationSelections, // For display - all selections by group
        variation_id: primaryVariation?.id || null // Direct access for booking payload
      };
      setSelectedServices([...selectedServices, serviceWithVariations]);
    }
  };

  // Calculate totals including variation prices
  const estimatedPrice = selectedServices.reduce((sum, service) => {
    const finalPrice = getServiceFinalPrice(service);
    return sum + finalPrice;
  }, 0);
  const estimatedDuration = selectedServices.reduce((sum, service) => sum + (service.duration_minutes || 0), 0);

  // Handle continue to next step
  const handleContinue = () => {
    if (selectedServices.length > 0 && canProceedToStep(3)) {
      // Sync any selected variations to the services in cart before proceeding
      // This ensures variation data is captured even if the callback chain failed
      const updatedServices = selectedServices.map(service => {
        const serviceVariationSelections = selectedVariations[service.id];

        // If no variations selected for this service, keep as is
        if (!serviceVariationSelections || Object.keys(serviceVariationSelections).length === 0) {
          return service;
        }

        // Get the primary variation
        const variations = serviceVariations[service.id] || [];
        let primaryVariation = null;

        for (const [groupId, selection] of Object.entries(serviceVariationSelections)) {
          if (selection && !Array.isArray(selection)) {
            const group = variations.find(g => g.id === groupId);
            primaryVariation = {
              id: selection.id,
              name: selection.name,
              groupName: group?.name || '',
              price_delta: selection.price_delta || 0,
              duration_delta: selection.duration_delta || 0
            };
            break;
          } else if (Array.isArray(selection) && selection.length > 0) {
            const group = variations.find(g => g.id === groupId);
            primaryVariation = {
              id: selection[0].id,
              name: selection[0].name,
              groupName: group?.name || '',
              price_delta: selection[0].price_delta || 0,
              duration_delta: selection[0].duration_delta || 0
            };
            break;
          }
        }

        // If we found a variation, update the service
        if (primaryVariation) {
          const finalPrice = calculateFinalPrice(service.price, serviceVariationSelections);
          const finalDuration = calculateFinalDuration(service.duration_minutes, serviceVariationSelections);

          return {
            ...service,
            price: finalPrice,
            duration_minutes: finalDuration,
            selectedVariation: primaryVariation,
            selectedVariations: serviceVariationSelections,
            variation_id: primaryVariation.id
          };
        }

        return service;
      });

      // Update store with synced services
      const hasChanges = updatedServices.some((s, i) =>
        s.selectedVariation && !selectedServices[i].selectedVariation
      );

      if (hasChanges) {
        setSelectedServices(updatedServices);
      }

      goToNextStep();
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header - Luxury Design */}
      <div className="flex-shrink-0 px-6 py-4 border-b border-brand-dark/10 dark:border-brand-light/10">
        <h2 className="text-lg font-serif font-bold text-brand-dark dark:text-brand-light tracking-wide">Selecionar Serviços</h2>
        <p className="text-sm text-brand-dark/60 dark:text-brand-light/60 mt-1 tracking-wide">Escolha os serviços que deseja agendar</p>
      </div>

      {/* Search - Luxury Design */}
      <div className="flex-shrink-0 px-6 py-4">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-brand-dark/40 dark:text-brand-light/40" size={20} strokeWidth={1.5} />
          <input
            ref={firstInputRef}
            type="text"
            value={serviceSearchQuery}
            onChange={(e) => setServiceSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3 text-base bg-transparent border border-brand-dark/20 dark:border-brand-light/20 text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light tracking-wide"
            placeholder="Buscar serviços..."
            inputMode="search"
          />
        </div>
      </div>

      {/* Shopping Cart Summary - Luxury Design */}
      {selectedServices.length > 0 && (
        <div className="flex-shrink-0 mx-6 mb-4 p-4 bg-brand-dark/5 dark:bg-brand-light/5 border border-brand-dark/20 dark:border-brand-light/20">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <ShoppingCart size={16} className="text-brand-dark dark:text-brand-light" strokeWidth={1.5} />
              <span className="text-sm text-brand-dark dark:text-brand-light font-medium tracking-wide">
                {selectedServices.length} serviço{selectedServices.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="flex items-center space-x-3">
              <span className="text-sm font-serif font-bold text-brand-dark dark:text-brand-light tracking-wide">
                {formatPrice(estimatedPrice)}
              </span>
              <button
                onClick={() => setSelectedServices([])}
                className="text-xs text-brand-dark dark:text-brand-light font-medium uppercase tracking-widest bg-transparent border border-brand-dark/20 dark:border-brand-light/20 px-2 py-1 hover:bg-brand-dark/5 dark:hover:bg-brand-light/5 transition-all duration-300"
              >
                Limpar
              </button>
            </div>
          </div>
          <div className="text-xs text-brand-dark/60 dark:text-brand-light/60 tracking-wide">
            Duração estimada: {formatDuration(estimatedDuration)}
          </div>
        </div>
      )}

      {/* Services List - Luxury Design */}
      <div className="flex-1 overflow-y-auto px-6 mobile-scroll">
        {loadingServices ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-2 border-brand-dark dark:border-brand-light border-t-transparent animate-spin rounded-full mx-auto mb-4"></div>
            <p className="text-brand-dark/60 dark:text-brand-light/60 tracking-wide">Carregando serviços...</p>
          </div>
        ) : (
          <div className="space-y-3 pb-4">
            {filteredServices.map((service) => {
              const selectedServiceInCart = selectedServices.find(s => s.id === service.id);
              const isSelected = !!selectedServiceInCart;
              const isExpanded = expandedServices.has(service.id);
              const variations = serviceVariations[service.id] || [];
              const serviceVariationSelections = selectedVariations[service.id] || {};
              const finalPrice = getServiceFinalPrice(service);
              // Get selected variation from cart if service is selected
              const selectedVariationInCart = selectedServiceInCart?.selectedVariation;

              return (
                <div key={service.id} className="space-y-2">
                  <div
                    onClick={() => toggleService(service)}
                    className={`p-4 border cursor-pointer transition-all duration-300 ${
                      isSelected
                        ? 'border-brand-dark dark:border-brand-light bg-brand-dark/5 dark:bg-brand-light/5'
                        : 'border-brand-dark/20 dark:border-brand-light/20 hover:bg-brand-dark/5 dark:hover:bg-brand-light/5'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-serif font-bold text-brand-dark dark:text-brand-light tracking-wide">{service.name}</h4>
                          {/* Show selected variation badge if service is in cart with a variation - clickable to change */}
                          {selectedVariationInCart && selectedVariationInCart.name && selectedVariationInCart.name !== 'Padrão' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleServiceExpansion(service.id);
                              }}
                              className="text-xs bg-green-600/10 dark:bg-green-400/10 text-green-600 dark:text-green-400 border border-green-600/30 dark:border-green-400/30 px-2 py-1 tracking-wide hover:bg-green-600/20 dark:hover:bg-green-400/20 transition-all"
                            >
                              {selectedVariationInCart.name} ✓
                            </button>
                          )}
                          {/* Show variations button if has variations and not selected yet */}
                          {variations.length > 0 && !selectedVariationInCart && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleServiceExpansion(service.id);
                              }}
                              className="text-xs bg-blue-600/10 dark:bg-blue-400/10 text-blue-600 dark:text-blue-400 border border-blue-600/30 dark:border-blue-400/30 px-2 py-1 uppercase tracking-wider hover:bg-blue-600/20 dark:hover:bg-blue-400/20 transition-all duration-300"
                            >
                              {variations.length} variação{variations.length !== 1 ? 'ões' : ''}
                            </button>
                          )}
                        </div>
                        <p className="text-sm text-brand-dark/60 dark:text-brand-light/60 mb-2 tracking-wide">{service.category_name}</p>
                        <div className="flex items-center gap-4">
                          <span className="text-sm text-brand-dark/60 dark:text-brand-light/60 flex items-center tracking-wide">
                            <Clock size={14} className="mr-1.5" strokeWidth={1.5} />
                            {formatDuration(service.duration_minutes)}
                          </span>
                          <span className="text-sm text-brand-dark dark:text-brand-light font-serif font-bold flex items-center tracking-wide">
                            <DollarSign size={14} className="mr-1" strokeWidth={1.5} />
                            {finalPrice !== parseFloat(service.price) ? (
                              <>
                                <span className="line-through text-brand-dark/40 dark:text-brand-light/40 mr-1">{formatPrice(service.price)}</span>
                                {formatPrice(finalPrice)}
                              </>
                            ) : (
                              formatPrice(service.price)
                            )}
                          </span>
                        </div>
                      </div>
                      <div className={`w-7 h-7 border-2 flex-shrink-0 mt-0.5 flex items-center justify-center transition-all duration-300 ${
                        isSelected ? 'bg-brand-dark dark:bg-brand-light border-brand-dark dark:border-brand-light' : 'border-brand-dark/30 dark:border-brand-light/30'
                      }`}>
                        {isSelected ? (
                          <Check size={16} className="text-brand-light dark:text-brand-dark" strokeWidth={2} />
                        ) : (
                          <Plus size={16} className="text-brand-dark/40 dark:text-brand-light/40" strokeWidth={1.5} />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Service Variations - Show when expanded - Luxury Design */}
                  {isExpanded && variations.length > 0 && (
                    <div className="ml-4 space-y-3 bg-brand-dark/5 dark:bg-brand-light/5 p-4 border border-brand-dark/10 dark:border-brand-light/10">
                      <div className="text-sm font-medium text-brand-dark dark:text-brand-light mb-2 uppercase tracking-wider">
                        Escolha as variações para {service.name}:
                      </div>
                      <ServiceVariations
                        variationGroups={variations}
                        selectedVariations={serviceVariationSelections}
                        onVariationSelect={(groupId, variation) => {
                          handleVariationSelect(service.id, groupId, variation);
                        }}
                        onVariationToggle={(groupId, variation) => handleVariationToggle(service.id, groupId, variation)}
                        size="small"
                        basePrice={parseFloat(service.price)}
                        baseDuration={service.duration_minutes || 0}
                      />
                    </div>
                  )}
                </div>
              );
            })}

            {!loadingServices && filteredServices.length === 0 && (
              <div className="text-center py-12">
                <p className="text-brand-dark/60 dark:text-brand-light/60 tracking-wide">Nenhum serviço encontrado</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Continue Button - Luxury Design */}
      <div className="flex-shrink-0 p-6 border-t border-brand-dark/10 dark:border-brand-light/10">
        <button
          onClick={handleContinue}
          disabled={selectedServices.length === 0}
          className={`w-full py-3 px-4 font-medium uppercase tracking-widest text-sm transition-all duration-300 ${
            selectedServices.length > 0
              ? 'bg-brand-dark dark:bg-brand-light text-brand-light dark:text-brand-dark border border-brand-dark dark:border-brand-light hover:bg-brand-dark/90 dark:hover:bg-brand-light/90'
              : 'bg-transparent border border-brand-dark/20 dark:border-brand-light/20 text-brand-dark/40 dark:text-brand-light/40 cursor-not-allowed'
          }`}
        >
          {selectedServices.length > 0
            ? `Continuar com ${selectedServices.length} serviço${selectedServices.length !== 1 ? 's' : ''}`
            : 'Selecione pelo menos um serviço'
          }
        </button>
      </div>
    </div>
  );
};

export default WizardServiceScreen;